import React, { createContext, Suspense, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loading } from '../../administration/components';
import { MENUE, NUR_LESEND_GRUND } from '../../config/ebenen';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { EinstLink, pfadVon } from '../../pages/einstellungen/EinstLink';
import BereichsSeite from '../BereichsSeite';
import type { VerweisProps } from '../SeitenRahmen';
import Dokumente, { useEigeneDokumente, useHilfeThemen } from './Dokumente';
import { ladeUebersicht, type HilfeUebersicht } from './hilfeApi';
import { HilfeKontext, type HilfeZustand } from './hilfeKontext';
import { HILFE_LEISTE, hilfeAdresse, hilfeEintragSichtbar, hilfeGruppen, hilfeSuche, type LeistenKontext } from './hilfeLeiste';
import { HILFE_TEXTE } from './hilfeTexte';
import './hilfe.css';

// Die Seite des Bereichs Hilfe als eigene Bereichsseite (Profil-Nachbau F-06 mit F-19, BAU-HILFE.md 1.1; PF-308-1): links im
// gemeinsamen Rahmen die Suche und die Einträge aus hilfeLeiste.ts, unten der Bereichsname, rechts die Unterseite. Seit F-26 ein
// eigenes großes Fenster mit „Schließen“ (BereichsSeite). Die Übersicht des Servers (Einträge, Zahlen, Punkte) lädt einmal beim Öffnen;
// fehlt sie, bleiben die Dokumente lesbar, weil sie aus dem Frontend kommen. Eingehängt unter /hilfe/* wird die Seite in
// App.tsx (PF-308-2); dort setzt unten, solange ein Pop-up darüber liegt.

// PF-309: der Chat lädt erst, wenn er geöffnet wird; ohne Assistenten führt er selbst zu den Dokumenten.
const HilfeChat = React.lazy(() => import('./HilfeChat'));
// PF-310-1: Liste und Formular der Tickets.
const Tickets = React.lazy(() => import('./Tickets'));
const TicketFormular = React.lazy(() => import('./TicketFormular'));
// PF-310-2: das Detail einer eigenen Anfrage.
const EigeneAnfrage = React.lazy(() => import('./TicketDetail'));
// PF-311-1: Störungen, Liste und Detail aus derselben Datei.
const Stoerungen = React.lazy(() => import('./Stoerungen'));
const StoerungSeite = React.lazy(() => import('./Stoerungen').then(modul => ({ default: modul.StoerungSeite })));
// PF-311-2: Wünsche – Liste, Formular, Detail.
const Wuensche = React.lazy(() => import('./Wuensche'));
const WunschEinreichen = React.lazy(() => import('./WunschFormular'));
const WunschDetail = React.lazy(() => import('./WunschDetail'));
// PF-312: Anfragen der Organisation; ohne Leserecht der Firma ersetzen sich beide durch „Tickets“.
const FirmenAnfragen = React.lazy(() => import('./FirmenAnfragen'));
const FirmenAnfrage = React.lazy(() => import('./FirmenAnfragen').then(modul => ({ default: modul.FirmenAnfrage })));
// PF-323: Neuigkeiten des Betreibers.
const Neuigkeiten = React.lazy(() => import('./Neuigkeiten'));

type Marke = { zahl?: number; punkt?: string };
// Zahl und Punkt je Ziel der Leiste, aus zahl und punkt der Leistentabelle.
const MarkenKontext = createContext<Map<string, Marke>>(new Map());

/** Der Link der Leiste: der Baustein der Bereichs-Ebene (Link-Regel, Rückfrage), dahinter Zahl und Punkt des Eintrags. So
 *  braucht der gemeinsame Rahmen keine eigene Stelle dafür. */
function HilfeVerweis({ children, ...rest }: VerweisProps) {
  const marke = useContext(MarkenKontext).get(pfadVon(rest.to));
  return <EinstLink {...rest}>
    {children}
    {(marke?.zahl ?? 0) > 0 && <span className="hilfe-zahl">{marke?.zahl}</span>}
    {marke?.punkt && <span className="hilfe-punkt"><span className="adm-sr-only">{marke.punkt}</span></span>}
  </EinstLink>;
}

/** Ohne Unterseite in der Adresse: die Dokumente; die Herkunft im Zustand bleibt (Anker und Abfrage entfallen). */
function ZuDokumente() {
  const { state } = useLocation();
  return <Navigate to={hilfeAdresse('dokumente')} replace state={state} />;
}

export default function HilfeSeite({ unten = false }: { unten?: boolean }) {
  const { user } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const [uebersicht, setUebersicht] = useState<HilfeUebersicht | null>(null);
  const [fehler, setFehler] = useState(false);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    const abbruch = new AbortController();
    setFehler(false);
    ladeUebersicht(abbruch.signal)
      .then(daten => { if (!abbruch.signal.aborted) setUebersicht(daten); })
      .catch(() => { if (!abbruch.signal.aborted) setFehler(true); });
    return () => abbruch.abort();
  }, [versuch]);

  const aktiveSicht = user?.sicht?.aktiv;
  const laden = uebersicht === null && !fehler;
  const zustand: HilfeZustand = { uebersicht, laden, fehler, nochmals: () => setVersuch(wert => wert + 1), sicht: !!aktiveSicht };
  const k: LeistenKontext = { uebersicht, organisation: brand.tenantLabel };
  const basis = hilfeGruppen(k, location.pathname.replace(/\/+$/, ''));
  const marken = new Map<string, Marke>(HILFE_LEISTE.map(zeile => [hilfeAdresse(zeile.bereich), { zahl: zeile.zahl?.(k), punkt: zeile.punkt?.(k) }]));
  // Die Suche der Leiste findet nur Fragen, die die Dokumente diesem Konto auch zeigen; PF-323: dazu die Titel der eigenen
  // Dokumente, sobald sie geladen sind.
  const { themen, bereit } = useHilfeThemen();
  const { eigene, fertig: eigeneFertig } = useEigeneDokumente();

  // Laden und Fehler der Übersicht stehen über den Einträgen der ersten Gruppe.
  const stand = laden
    ? <p className="hilfe-leiste-stand" role="status" aria-busy="true" data-laden>{HILFE_TEXTE.laden}</p>
    : fehler
      ? <div className="hilfe-leiste-stand hilfe-leiste-fehler" role="alert">
        <p>{HILFE_TEXTE.fehler}</p>
        <button type="button" className="hilfe-knopf" onClick={zustand.nochmals}>{HILFE_TEXTE.erneutVersuchen}</button>
      </div>
      : null;
  const gruppen = stand && basis.length > 0 ? [{ ...basis[0], zusatz: stand }, ...basis.slice(1)] : basis;

  return <HilfeKontext.Provider value={zustand}>
    <MarkenKontext.Provider value={marken}>
      <BereichsSeite unten={unten} rahmenKlasse="einst-dialog einst-seite hilfe-seite" verweis={HilfeVerweis}
        lesegrenze={aktiveSicht ? NUR_LESEND_GRUND(aktiveSicht.linse) : undefined}
        leiste={{ leisteName: MENUE.hilfeUndFeedback, inhaltName: MENUE.hilfeUndFeedback, bereichsName: MENUE.hilfeUndFeedback, schliessbar: true,
          gruppen,
          suche: { label: HILFE_TEXTE.suche.label, platzhalter: HILFE_TEXTE.suche.platzhalter,
            leerText: hilfeEintragSichtbar('chat', k) ? HILFE_TEXTE.suche.leer : HILFE_TEXTE.suche.leerOhneChat,
            filtern: text => hilfeSuche(basis, themen, text, eigene) } }}>
        {/* Jede Unterseite trägt hier ihre Route und in hilfeLeiste.ts ihre Zeile ein. */}
        <Routes>
          <Route path="dokumente" element={<Dokumente themen={themen} bereit={bereit} eigene={eigene} eigeneFertig={eigeneFertig} />} />
          <Route path="chat" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><HilfeChat /></Suspense>} />
          <Route path="neuigkeiten" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><Neuigkeiten /></Suspense>} />
          <Route path="tickets" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><Tickets /></Suspense>} />
          <Route path="tickets/neu" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><TicketFormular /></Suspense>} />
          <Route path="tickets/:referenz" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><EigeneAnfrage /></Suspense>} />
          <Route path="stoerungen" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><Stoerungen /></Suspense>} />
          <Route path="stoerungen/:id" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><StoerungSeite /></Suspense>} />
          <Route path="wuensche" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><Wuensche /></Suspense>} />
          <Route path="wuensche/neu" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><WunschEinreichen /></Suspense>} />
          <Route path="wuensche/:referenz" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><WunschDetail /></Suspense>} />
          <Route path="organisation" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><FirmenAnfragen /></Suspense>} />
          <Route path="organisation/:referenz" element={<Suspense fallback={<Loading text={HILFE_TEXTE.laden} />}><FirmenAnfrage /></Suspense>} />
          <Route index element={<ZuDokumente />} />
          <Route path="*" element={<ZuDokumente />} />
        </Routes>
      </BereichsSeite>
    </MarkenKontext.Provider>
  </HilfeKontext.Provider>;
}
